// utils
import { axiosServerData, NetsuiteInstanceProps } from 'src/utils/axios';
// types
import { IInvoice } from 'src/types/invoice';
// locks
import { acquireReturnLock, releaseReturnLock } from './return-locks';
// ----------------------------------------------------------------------
const param = { firstEndPoint: 'cashier' } as NetsuiteInstanceProps
const initParam = ({ idUser, data, endUrl, lastEndPoint, method }: NetsuiteInstanceProps) => {
  param.data = data
  param.endUrl = endUrl || ''
  param.idUser = idUser
  param.method = method
  param.headers = {};
  param.lastEndPoint = lastEndPoint;
}

/**
 * Génère une clé d'idempotency au format standard BAE.
 * Format : `IDEM-${timestampBase36}-${randomBase36}`
 *
 * Cette clé est utilisée par le backend (MongoDB + Netsuite) pour :
 *  - rejeter les requêtes dupliquées (idempotency check)
 *  - libérer les locks de retour uniquement après confirmation
 *
 * La clé est volontairement exportée pour permettre :
 *  - la mise en cache locale (éviter deux soumissions successives)
 *  - la réutilisation après une perte de connexion réseau
 *
 * @returns {string} Une clé unique d'idempotency.
 */
export const generateIdempotencyKey = (): string => {
  const ts = Date.now().toString(36);
  const rand = Math.random().toString(36).slice(2, 11);
  return `IDEM-${ts}-${rand}`;
};

const STORAGE_PREFIX = '__idem_return__';

/**
 * Récupère une clé d'idempotency existante pour une transaction donnée.
 * Si absente, en génère une nouvelle et la persiste.
 *
 * @param {string} transactionNumber - identifiant fonctionnel du retour
 * @param {string} [providedKey] - clé fournie par l'appelant (prioritaire)
 */
export const getOrCreateIdempotencyKey = (
  transactionNumber: string,
  providedKey?: string
): string => {
  if (providedKey) {
    try {
      window.localStorage.setItem(`${STORAGE_PREFIX}${transactionNumber}`, providedKey);
    } catch (_) {
      // localStorage peut être indisponible (mode privé) ; on continue sans persistance
    }
    return providedKey;
  }

  try {
    const stored = window.localStorage.getItem(`${STORAGE_PREFIX}${transactionNumber}`);
    if (stored) return stored;
  } catch (_) {
    // ignore
  }

  const fresh = generateIdempotencyKey();
  try {
    window.localStorage.setItem(`${STORAGE_PREFIX}${transactionNumber}`, fresh);
  } catch (_) {
    // ignore
  }
  return fresh;
};

/**
 * Libère la clé d'idempotency après succès (ou annulation définitive).
 * Permet à l'utilisateur de représenter la même action si nécessaire.
 */
export const clearIdempotencyKey = (transactionNumber: string): void => {
  try {
    window.localStorage.removeItem(`${STORAGE_PREFIX}${transactionNumber}`);
  } catch (_) {
    // ignore
  }
};

export const createTransaction = async (idUser: string, config: string, data: any) => {
  console.log('createTransaction required :', { idUser, config });
  if(!data.transactionNumber) data.transactionNumber = `TXN-${Date.now()}`
  // Generate idempotency key if not provided (use transactionNumber as fallback)
  if(!data.idempotencyKey) data.idempotencyKey = data.transactionNumber || `IDEM-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
  initParam({
    data,
    idUser,
    lastEndPoint: 'transactions',
    method: 'post',
  } as NetsuiteInstanceProps)
  param.headers = { config }
  return (await axiosServerData(param)) as { success: boolean, data: IInvoice, transaction: any, error?: string, idempotent?: boolean };
};

/**
 * Crée un retour (avec ou sans vente) en garantissant l'idempotency.
 *
 * Si `data.idempotencyKey` est fourni, il est utilisé tel quel et persistant.
 * Sinon, une clé est générée et stockée dans le localStorage du navigateur
 * pour empêcher les soumissions multiples (deux clics rapides sur
 * "Confirmer le retour", double-tap mobile, problème réseau, etc.).
 *
 * La fonction réutilise systématiquement la même clé tant que l'appel n'a
 * pas abouti. Une fois la réponse reçue avec succès, le frontend peut
 * appeler `clearIdempotencyKey(transactionNumber)` pour réinitialiser.
 *
 * @param idUser    Identifiant Cognito de l'utilisateur
 * @param config    Identifiant de la configuration active
 * @param data      Payload de retour (transactionNumber, items, refunds, etc.)
 */
export const createReturn = async (idUser: string, config: string, data: any) => {
  if(!data.transactionNumber) data.transactionNumber = `TXN-${Date.now()}`

  // 1.1 — Idempotency : récupère / génère / persiste une clé pour ce retour
  data.idempotencyKey = getOrCreateIdempotencyKey(data.transactionNumber, data.idempotencyKey);

  initParam({
    data,
    idUser,
    lastEndPoint: 'return',
    method: 'post',
  } as NetsuiteInstanceProps)
  param.headers = { config }
  return (await axiosServerData(param)) as {
    success: boolean,
    data: any,
    transaction: any,
    error?: string,
    idempotent?: boolean,
    locked?: boolean,
  };
};

/**
 * Crée un retour en englobant l'appel dans un verrou idempotent.
 *
 * Séquence :
 * 1. Vérifie / génère la clé d'idempotency
 * 2. Tente d'acquérir le lock frontend (30 s TTL)
 *    - ALREADY_DONE → retourne directement la réponse précédente
 *    - ALREADY_PENDING → attend brièvement puis retente (1 fois)
 * 3. Appelle createReturn (avec la clé persistée côté backend)
 * 4. Relâche le lock : COMPLETED si succès, FAILED si erreur
 *
 * @returns Le résultat de createReturn, ou un objet { lockResult } si bloqué.
 */
export const createReturnWithLock = async (idUser: string, config: string, data: any) => {
  // 1. Garantir une clé d'idempotency
  if (!data.transactionNumber) {
    data.transactionNumber = `RETURN-${Date.now()}`;
  }
  data.idempotencyKey = getOrCreateIdempotencyKey(data.transactionNumber, data.idempotencyKey);

  // 2. Tentative d'acquisition du lock
  const lockResult = acquireReturnLock(data.idempotencyKey);

  if (lockResult.result === 'ALREADY_DONE') {
    // Retour déjà finalisé — on nettoie et retourne une réponse idempotente
    return {
      success: true,
      idempotent: true,
      data: { alreadyProcessed: true },
      error: undefined,
    };
  }

  if (lockResult.result === 'ALREADY_PENDING') {
    // Soumission en cours — Petit délai puis retente
    await new Promise(r => setTimeout(r, 800));
    // Vérifier si c'est maintenant DONE
    const recheck = acquireReturnLock(data.idempotencyKey);
    if (recheck.result === 'ALREADY_DONE') {
      return {
        success: true,
        idempotent: true,
        data: { alreadyProcessed: true },
        error: undefined,
      };
    }
    // Si toujours PENDING, on poursuit malgré tout (le backend réjectera)
  }

  if (lockResult.result === 'ERROR') {
    return {
      success: false,
      data: null,
      error: lockResult.reason,
      idempotent: false,
    };
  }

  // 3. Appel API (lock acquis)
  try {
    const response = await createReturn(idUser, config, data);
    const failed =
      !response?.success ||
      Boolean(response?.error) ||
      Boolean((response as any)?.warning) ||
      response?.transaction?.syncStatus === 'failed';

    // Une création locale avec synchronisation ERP en échec ne doit jamais
    // devenir ALREADY_DONE côté navigateur : l'interface doit rester en erreur.
    releaseReturnLock(data.idempotencyKey, failed ? 'FAILED' : 'COMPLETED');
    return response;
  } catch (error) {
    // 4b. Erreur → lock FAILED (permet retry)
    releaseReturnLock(data.idempotencyKey, 'FAILED');
    throw error;
  }
};

export const getTransactions = async (
  idUser: string,
  config: string,
  page: number,
  includeCancelled = false
) => {
  try {
    initParam({idUser, lastEndPoint: 'transactions', method: 'get'} as NetsuiteInstanceProps)
    param.headers = { config }
    param.endUrl = `?config=${config}&page=${page}&includeCancelled=${includeCancelled ? 'true' : 'false'}`;

    return await axiosServerData(param)
  } catch (error) {
    console.error('Error fetching transactions:', error)
    // Retourner une structure de données vide en cas d'erreur
    // Gérer spécifiquement l'erreur "Account ERP not found" ou "Cash register not found"
    const errorMessage = error?.message || error?.error || 'Failed to fetch transactions'
    if (errorMessage.includes('Account ERP not found') || errorMessage.includes('Cash register not found') || errorMessage.includes('No CashRegister')) {
      return {
        transactions: [],
        pagination: { nextPage: null },
        error: 'No cash register or config assigned to this user'
      }
    }
    return {
      transactions: [],
      pagination: { nextPage: null },
      error: errorMessage
    }
  }
}
